import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  ROLES,
  api,
  atLeast,
  roleMeans,
  roleName,
  type Group,
  type Member,
  type Role,
  type User,
} from "../api";

/**
 * Who may touch this project, or this rig.
 *
 * ONE COMPONENT FOR BOTH SCOPES, because they are the same list with the same
 * four roles and the same right to edit it - and because the only difference
 * that matters (a bench also shows what it INHERITS) is the thing a second
 * implementation would get wrong first.
 *
 * WHAT INHERITED ROWS ARE FOR. A bench page that showed only its own grants
 * would answer "who may reboot this rig" with a list that is usually empty,
 * and the true answer - everyone the project let in - would be one click away
 * on another page. They are shown greyed, with the project named, and they
 * cannot be removed from here: withdrawing one would take access to every other
 * rig in the project away from a page about this one.
 *
 * WHY THE ROLE IS A SELECT AND NOT AN "EDIT" BUTTON. Re-granting is idempotent
 * on the server - the same call adds and changes - so a dropdown that saves on
 * change is the whole interaction, and there is no half-edited state to
 * reconcile.
 */
export default function Members({
  scope,
  id,
  members,
  inherited,
  myRole,
  onChanged,
}: {
  scope: "project" | "bench";
  id: number;
  members: Member[];
  inherited?: Member[];
  /** This reader's own role here. Owner is what buys the editing. */
  myRole: Role | null | undefined;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const mayEdit = atLeast(myRole, "owner");
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);

  async function change(m: Member, role: Role) {
    setError("");
    try {
      await api.addMember(
        scope, id,
        m.kind === "user" ? { user_id: m.subject_id } : { group_id: m.subject_id },
        role,
      );
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function remove(m: Member) {
    setError("");
    try {
      await api.removeMember(scope, id, m.id);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  return (
    <section className="mt-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
          {t("members.heading", { count: members.length })}
        </h2>
        {mayEdit && !adding && (
          <button
            className="rounded border border-slate-300 px-2 py-1 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
            onClick={() => setAdding(true)}
          >
            {t("members.add")}
          </button>
        )}
        {!mayEdit && (
          <span className="text-xs text-slate-500">
            {/* Said out loud rather than shown as a page with no buttons, which
                reads as a feature that is missing rather than one that is not
                theirs. */}
            {t("members.needOwner")}
          </span>
        )}
      </div>

      {error && (
        <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          {error}
        </p>
      )}

      {adding && (
        <AddMember
          scope={scope}
          id={id}
          onDone={() => {
            setAdding(false);
            onChanged();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {members.length === 0 && !adding && (
        <p className="mt-2 text-sm text-slate-500">
          {scope === "bench" ? t("members.emptyBench") : t("members.emptyProject")}
        </p>
      )}

      {members.length > 0 && (
        <ul className="mt-2 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
          {members.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <Subject m={m} />
              {mayEdit ? (
                <select
                  aria-label={t("members.roleFor", { name: m.name })}
                  className="ml-auto rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-950"
                  value={m.role}
                  onChange={(e) => void change(m, e.target.value as Role)}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {roleName(r)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ml-auto text-sm text-slate-500">
                  {roleName(m.role)}
                </span>
              )}
              {mayEdit && (
                <button
                  className="text-sm text-red-700 hover:underline dark:text-red-400"
                  onClick={() => void remove(m)}
                >
                  {t("members.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {inherited && inherited.length > 0 && (
        <>
          <h3 className="mt-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t("members.inherited", { count: inherited.length })}
          </h3>
          <ul className="mt-1 divide-y divide-slate-200 rounded-lg border border-dashed border-slate-300 dark:divide-slate-800 dark:border-slate-700">
            {inherited.map((m) => (
              <li
                key={`i${m.id}`}
                className="flex flex-wrap items-center gap-3 px-3 py-2 text-slate-500"
              >
                <Subject m={m} />
                <span className="ml-auto text-sm">{roleName(m.role)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-slate-500">
            {t("members.inheritedNote")}
          </p>
        </>
      )}

      <RoleKey />
    </section>
  );
}

function Subject({ m }: { m: Member }) {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-2">
      <span
        className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        title={
          m.kind === "group"
            ? t("members.kindGroupTitle")
            : t("members.kindPersonTitle")
        }
      >
        {t(`members.kind.${m.kind}`)}
      </span>
      <span className="font-medium">{m.name}</span>
      {m.detail && <span className="text-xs text-slate-500">{m.detail}</span>}
      {m.inactive && (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-900 dark:bg-amber-900 dark:text-amber-100">
          {t("members.deactivated")}
        </span>
      )}
    </span>
  );
}

/** What the four words mean, once, under the list that uses them. */
/**
 * What the four rungs buy - BEHIND A DISCLOSURE.
 *
 * It was body text, printed in full on every bench page and every project page,
 * every visit. That is reference material: the answer to a question somebody
 * asks once, and then never again, occupying four lines of a page they read
 * daily. Shut by default, one click from the person who has not met the model
 * yet, and the summary says which question it answers.
 */
function RoleKey() {
  const { t } = useTranslation();
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-xs text-slate-500">
        {t("members.roleKeySummary")}
      </summary>
      <dl className="mt-2 grid gap-x-3 gap-y-1 text-xs text-slate-500 sm:grid-cols-2">
        {ROLES.map((r) => (
          <div key={r} className="flex gap-2">
            <dt className="font-medium text-slate-600 dark:text-slate-400">
              {roleName(r)}
            </dt>
            <dd>{roleMeans(r)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/**
 * The add form: a person or a team, and a role.
 *
 * The pickers are loaded from the server rather than typed, because a name
 * typed wrong is a 404 the person then has to interpret - and because the
 * server's list is what "everyone who has an account" actually is.
 */
function AddMember({
  scope,
  id,
  onDone,
  onCancel,
}: {
  scope: "project" | "bench";
  id: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [kind, setKind] = useState<"user" | "group">("user");
  const [subject, setSubject] = useState("");
  const [role, setRole] = useState<Role>("guest");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [u, g] = await Promise.all([api.users(), api.groups()]);
      setUsers(u.users);
      setGroups(g.groups);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const options = useMemo(
    () =>
      kind === "user"
        ? users.map((u) => ({ id: u.id, label: u.display_name ? `${u.name} — ${u.display_name}` : u.name }))
        : groups.map((g) => ({ id: g.id, label: `${g.name} (${g.member_count})` })),
    [kind, users, groups],
  );

  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
      onSubmit={async (e) => {
        e.preventDefault();
        setError("");
        try {
          await api.addMember(
            scope, id,
            kind === "user" ? { user_id: Number(subject) } : { group_id: Number(subject) },
            role,
          );
          onDone();
        } catch (err) {
          setError(err instanceof ApiError ? err.message : String(err));
        }
      }}
    >
      <label className="text-sm">
        <span className="mb-1 block text-slate-600 dark:text-slate-400">
          {t("members.grantTo")}
        </span>
        <select
          aria-label={t("members.subjectKind")}
          className="rounded-md border border-slate-300 bg-white px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as "user" | "group");
            setSubject("");
          }}
        >
          <option value="user">{t("members.aPerson")}</option>
          <option value="group">{t("members.aGroup")}</option>
        </select>
      </label>

      <label className="min-w-[12rem] flex-1 text-sm">
        <span className="mb-1 block text-slate-600 dark:text-slate-400">
          {kind === "user" ? t("members.person") : t("members.group")}
        </span>
        <select
          aria-label={t("members.subject")}
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        >
          <option value="">{t("members.choose")}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <label className="text-sm">
        <span className="mb-1 block text-slate-600 dark:text-slate-400">
          {t("members.role")}
        </span>
        <select
          aria-label={t("members.role")}
          className="rounded-md border border-slate-300 bg-white px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {roleName(r)} — {roleMeans(r)}
            </option>
          ))}
        </select>
      </label>

      <button
        disabled={!subject}
        className="rounded-md bg-slate-900 px-3 py-2 text-sm text-white disabled:opacity-40 dark:bg-slate-100 dark:text-slate-900"
      >
        {t("members.addButton")}
      </button>
      <button type="button" className="px-2 py-2 text-sm" onClick={onCancel}>
        {t("common.cancel")}
      </button>
      {error && (
        <p className="w-full text-sm text-red-700 dark:text-red-400">{error}</p>
      )}
    </form>
  );
}
